export interface GoogleAdsAddress {
  first_name: string;
  last_name: string;
  postal_code: string;
  country: string;
  street?: string;
  city?: string;
  region?: string;
}

export interface GoogleAdsUserData {
  email?: string;
  address?: GoogleAdsAddress;
}

export interface StripeBillingAddressInput {
  line1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export interface StripeBillingDetailsInput {
  name?: string | null;
  address?: StripeBillingAddressInput | null;
}

export interface SanitizedStripeBillingDetails {
  name?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

export interface GoogleAdsGeoData {
  country?: string | null;
  city?: string | null;
  region?: string | null;
}

export interface BuildGoogleAdsUserDataParams {
  email?: string | null;
  profileName?: string | null;
  stripeBillingDetails?: StripeBillingDetailsInput | null;
  geo?: GoogleAdsGeoData | null;
}

export const withGoogleAdsUserData = (
  payload: Record<string, unknown>,
  userData?: GoogleAdsUserData,
): Record<string, unknown> => {
  if (!userData?.email && !userData?.address) return payload;

  return {
    ...payload,
    user_data: userData,
  };
};

const nonEmpty = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

const normalizeCountry = (value?: string | null): string | undefined => {
  const country = nonEmpty(value)?.toUpperCase();
  return country && /^[A-Z]{2}$/.test(country) ? country : undefined;
};

const splitFullName = (value?: string | null): { firstName?: string; lastName?: string } => {
  const parts = nonEmpty(value)?.split(/\s+/) ?? [];
  const [firstName, ...lastNameParts] = parts;
  const lastName = lastNameParts.join(' ') || undefined;

  return { firstName, lastName };
};

export const sanitizeStripeBillingDetails = (
  details?: StripeBillingDetailsInput | null,
): SanitizedStripeBillingDetails | undefined => {
  if (!details) return undefined;

  const name = nonEmpty(details.name);
  const line1 = nonEmpty(details.address?.line1);
  const city = nonEmpty(details.address?.city);
  const state = nonEmpty(details.address?.state);
  const postalCode = nonEmpty(details.address?.postal_code);
  const country = normalizeCountry(details.address?.country);
  const hasAddress = !!(line1 || city || state || postalCode || country);

  if (!name && !hasAddress) return undefined;

  return {
    ...(name && { name }),
    ...(hasAddress && {
      address: {
        ...(line1 && { line1 }),
        ...(city && { city }),
        ...(state && { state }),
        ...(postalCode && { postal_code: postalCode }),
        ...(country && { country }),
      },
    }),
  };
};

export const buildGoogleAdsUserData = ({
  email,
  profileName,
  stripeBillingDetails,
  geo,
}: BuildGoogleAdsUserDataParams): GoogleAdsUserData | undefined => {
  const normalizedEmail = nonEmpty(email)?.toLowerCase();
  const billingDetails = sanitizeStripeBillingDetails(stripeBillingDetails);
  const billingName = splitFullName(billingDetails?.name);
  const fallbackName = splitFullName(profileName);
  const firstName = billingName.firstName || fallbackName.firstName;
  const lastName = billingName.lastName || fallbackName.lastName;
  const postalCode = nonEmpty(billingDetails?.address?.postal_code);
  const country =
    normalizeCountry(billingDetails?.address?.country) || normalizeCountry(geo?.country);
  const street = nonEmpty(billingDetails?.address?.line1);
  const city = nonEmpty(billingDetails?.address?.city) || nonEmpty(geo?.city);
  const region = nonEmpty(billingDetails?.address?.state) || nonEmpty(geo?.region);
  const hasCompleteAddress = !!(firstName && lastName && postalCode && country);

  if (!normalizedEmail && !hasCompleteAddress) return undefined;

  return {
    ...(normalizedEmail && { email: normalizedEmail }),
    ...(hasCompleteAddress && {
      address: {
        first_name: firstName,
        last_name: lastName,
        postal_code: postalCode,
        country,
        ...(street && { street }),
        ...(city && { city }),
        ...(region && { region }),
      },
    }),
  };
};
